import type { ChapterVerse } from '../audio/biblebrain.js';
import { atlasIndexSignal, type AtlasLink } from '../data/verse-atlas-links.js';
import { AtlasLinkSpan } from './AtlasLinkSpan.js';
import { bookmarks } from '../state/bookmarks-store.js';
import { highlights } from '../state/highlights-store.js';
import { notes } from '../state/notes-store.js';
import { readingPrefs } from '../state/reading-prefs.js';

type Props = {
  verse: ChapterVerse;
  lang: string;
  book: string;
  active: boolean;
  progress: number;
  onTap: (ref: string) => void;
  /** When true the verse flows inline inside a paragraph block instead of
   *  occupying its own block row. The outer element becomes a <span> so verse
   *  text runs continuously. All existing interactivity (tap menu, highlights,
   *  bookmarks, karaoke) remains per-verse — the `verse` class and
   *  `data-verse-ref` attribute are kept on the inner wrapper span. */
  inline?: boolean;
};

type Chunk =
  | { kind: 'text'; value: string }
  | { kind: 'atlas'; link: AtlasLink; surface: string };

type WordToken = { leading: string; word: string; trailing: string };

type VersePlan = {
  chunks: Chunk[];
  /** Tokenized words per text-chunk, parallel to `chunks`. `null` for atlas chunks. */
  tokensByChunk: (WordToken[] | null)[];
  /** Total word count (words + atlas surfaces) across the verse, for karaoke math. */
  totalWords: number;
  /**
   * Cumulative character-fraction boundary for each word across the verse.
   * charBoundaries[i] is the point (0–1) at which word i finishes speaking.
   * Longer words get proportionally more time than short function words,
   * making literacy-mode highlighting far more accurate than a uniform sweep.
   */
  charBoundaries: number[];
};

/**
 * `splitVerseByLinks` + per-chunk tokenization are O(text-length × link-count)
 * and used to run on every karaoke tick for every verse on screen. Cache the
 * plan by verse object identity so re-renders are O(1) and word-level work
 * is only paid once per chapter load.
 *
 * Keyed by the verse object reference, which is stable across re-renders as
 * long as the chapter store hasn't replaced it.
 */
const versePlanCache = new WeakMap<ChapterVerse, { links: AtlasLink[]; plan: VersePlan }>();

function getVersePlan(verse: ChapterVerse, links: AtlasLink[]): VersePlan {
  const cached = versePlanCache.get(verse);
  if (cached && cached.links === links) return cached.plan;
  const chunks = splitVerseByLinks(verse.text, links);
  const tokensByChunk = chunks.map((c) => (c.kind === 'text' ? tokenize(c.value) : null));
  let totalWords = 0;
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    if (chunk?.kind === 'text') totalWords += tokensByChunk[i]?.length ?? 0;
    else if (chunk?.kind === 'atlas') totalWords += Math.max(1, countWords(chunk.surface));
  }
  const charBoundaries = buildCharBoundaries(chunks, tokensByChunk);
  const plan = { chunks, tokensByChunk, totalWords, charBoundaries };
  versePlanCache.set(verse, { links, plan });
  return plan;
}

export function Verse({ verse, lang, book, active, progress, onTap, inline = false }: Props) {
  const links = atlasIndexSignal(book, lang).value[verse.ref] ?? EMPTY_LINKS;
  const plan = getVersePlan(verse, links);
  const literacy = readingPrefs.value.literacyMode;
  const bookmarked = bookmarks.value.some((b) => b.ref === verse.ref);
  const highlight = highlights.value[verse.ref] ?? null;
  const hasNote = notes.value[verse.ref] != null;

  const wrapperClass = `verse${active ? ' verse-active' : ''}${bookmarked ? ' verse-bookmarked' : ''}${
    highlight ? ` verse-highlight verse-highlight-${highlight}` : ''
  }${hasNote ? ' verse-has-note' : ''}`;

  // ──────────────────────────────────────────────────────────
  // Fast path: not active, OR literacy mode off.
  // Render plain text + atlas-link spans. No `.verse-word` per-token
  // spans at all — saves N×token DOM nodes per verse and skips the
  // per-tick word-class flip work.
  // ──────────────────────────────────────────────────────────
  if (!active || !literacy) {
    // Inline mode: the verse flows as a <span> inside a paragraph block.
    // We keep `data-verse-ref` and the full `wrapperClass` so tap-to-open,
    // highlights, bookmarks, and karaoke scroll-targeting all keep working.
    if (inline) {
      return (
        <span
          class={wrapperClass}
          data-verse-ref={verse.ref}
          onClick={() => onTap(verse.ref)}
        >
          <sup class="verse-num" aria-hidden="true">{verse.number}</sup>{' '}
          {plan.chunks.map((c, ci) =>
            c.kind === 'text' ? (
              <span key={ci}>{c.value}</span>
            ) : (
              <AtlasLinkSpan
                key={ci}
                link={c.link}
                lang={lang}
                text={c.surface}
                verseRef={verse.ref}
                extraClass=""
              />
            ),
          )}
          {hasNote ? <span class="verse-note-glyph" aria-label="Has note">✎</span> : null}
        </span>
      );
    }
    return (
      <p
        class={wrapperClass}
        data-verse-ref={verse.ref}
        onClick={() => onTap(verse.ref)}
      >
        <span class="verse-num" aria-hidden="true">{verse.number}</span>{' '}
        {plan.chunks.map((c, ci) =>
          c.kind === 'text' ? (
            <span key={ci}>{c.value}</span>
          ) : (
            <AtlasLinkSpan
              key={ci}
              link={c.link}
              lang={lang}
              text={c.surface}
              verseRef={verse.ref}
              extraClass=""
            />
          ),
        )}
        {hasNote ? <span class="verse-note-glyph" aria-label="Has note">✎</span> : null}
      </p>
    );
  }

  // ──────────────────────────────────────────────────────────
  // Word-level karaoke (active + literacy). This is the heavy path
  // — only one verse on the page hits it at a time, and only when
  // the user has explicitly enabled literacy mode.
  // ──────────────────────────────────────────────────────────
  const sweep = Math.max(0, Math.min(1, progress));
  // Character-weighted word index: find the first word whose cumulative
  // character boundary exceeds `sweep`. Longer words get proportionally more
  // time than short function words, so "Nebuchadnezzar" doesn't flash past
  // at the same rate as "the".
  let spokenWordIndex = -1;
  const { charBoundaries } = plan;
  if (charBoundaries.length > 0) {
    spokenWordIndex = charBoundaries.length - 1;
    for (let i = 0; i < charBoundaries.length; i++) {
      if (sweep < (charBoundaries[i] ?? 1)) { spokenWordIndex = i; break; }
    }
  }

  let wordCursor = 0;
  const rendered = plan.chunks.map((c, ci) => {
    if (c.kind === 'text') {
      const tokens = plan.tokensByChunk[ci] ?? [];
      const spans = tokens.map((t, ti) => {
        const i = wordCursor++;
        const cls =
          i < spokenWordIndex
            ? 'verse-word is-spoken'
            : i === spokenWordIndex
              ? 'verse-word is-current'
              : 'verse-word';
        return (
          <span key={`${ci}-${ti}`} class={cls}>
            {t.leading}
            {t.word}
            {t.trailing}
          </span>
        );
      });
      return <span key={ci}>{spans}</span>;
    }
    const i = wordCursor;
    wordCursor += Math.max(1, countWords(c.surface));
    const linkClass =
      i < spokenWordIndex ? 'is-spoken' : i === spokenWordIndex ? 'is-current' : '';
    return (
      <AtlasLinkSpan
        key={ci}
        link={c.link}
        lang={lang}
        text={c.surface}
        verseRef={verse.ref}
        extraClass={linkClass}
      />
    );
  });

  // Karaoke active + literacy mode — inline variant keeps the <span> outer
  // element so karaoke word highlighting still works inside a paragraph flow.
  if (inline) {
    return (
      <span
        class={wrapperClass}
        data-verse-ref={verse.ref}
        style={{ '--karaoke-sweep': `${sweep * 100}%` }}
        onClick={() => onTap(verse.ref)}
      >
        <sup class="verse-num" aria-hidden="true">{verse.number}</sup>{' '}
        {rendered}
        {hasNote ? <span class="verse-note-glyph" aria-label="Has note">✎</span> : null}
      </span>
    );
  }

  return (
    <p
      class={wrapperClass}
      data-verse-ref={verse.ref}
      style={{ '--karaoke-sweep': `${sweep * 100}%` }}
      onClick={() => onTap(verse.ref)}
    >
      <span class="verse-num" aria-hidden="true">{verse.number}</span>{' '}
      {rendered}
      {hasNote ? <span class="verse-note-glyph" aria-label="Has note">✎</span> : null}
    </p>
  );
}

const EMPTY_LINKS: AtlasLink[] = [];

function tokenize(text: string): WordToken[] {
  const out: WordToken[] = [];
  const regex = /(\s*)(\S+)(\s*)/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    out.push({ leading: m[1] ?? '', word: m[2] ?? '', trailing: m[3] ?? '' });
  }
  return out;
}

function countWords(text: string): number {
  const m = text.match(/\S+/g);
  return m ? m.length : 0;
}

function buildCharBoundaries(chunks: Chunk[], tokensByChunk: (WordToken[] | null)[]): number[] {
  const charCounts: number[] = [];
  for (let ci = 0; ci < chunks.length; ci++) {
    const c = chunks[ci];
    if (c?.kind === 'text') {
      const tokens = tokensByChunk[ci] ?? [];
      for (const t of tokens) charCounts.push(Math.max(1, t.word.length));
    } else if (c?.kind === 'atlas') {
      const wc = Math.max(1, countWords(c.surface));
      const perWord = Math.max(1, Math.ceil(c.surface.length / wc));
      for (let w = 0; w < wc; w++) charCounts.push(perWord);
    }
  }
  if (charCounts.length === 0) return [];
  const total = charCounts.reduce((a, b) => a + b, 0);
  let cum = 0;
  return charCounts.map((c) => { cum += c; return cum / total; });
}

function splitVerseByLinks(text: string, links: AtlasLink[]): Chunk[] {
  if (links.length === 0) return [{ kind: 'text', value: text }];

  const matches: Array<{ start: number; end: number; link: AtlasLink; surface: string }> = [];
  for (const link of links) {
    let idx = 0;
    while (idx < text.length) {
      const found = text.indexOf(link.surface, idx);
      if (found === -1) break;
      matches.push({
        start: found,
        end: found + link.surface.length,
        link,
        surface: link.surface,
      });
      idx = found + link.surface.length;
    }
  }

  matches.sort((a, b) => a.start - b.start);
  const merged: typeof matches = [];
  for (const m of matches) {
    const last = merged[merged.length - 1];
    if (!last || m.start >= last.end) merged.push(m);
  }

  const chunks: Chunk[] = [];
  let cursor = 0;
  for (const m of merged) {
    if (m.start > cursor) {
      chunks.push({ kind: 'text', value: text.slice(cursor, m.start) });
    }
    chunks.push({ kind: 'atlas', link: m.link, surface: m.surface });
    cursor = m.end;
  }
  if (cursor < text.length) chunks.push({ kind: 'text', value: text.slice(cursor) });
  return chunks;
}
